/**
 * Command buttons (Button, IconButton) inside the kit's wrappers.
 *
 * The contract says a button with `command` runs it on click unless the caller gave `onClick`. A Radix trigger
 * rendered with `asChild` merges its own click handler (Popover toggles, Tooltip closes) into the child's
 * `onClick` through Slot, so inside the button that handler looks exactly like a caller's `onClick` and the
 * command would never run. Two mechanisms keep the contract:
 *
 *  - `asTriggerChild` (Popover, Menu, ContextMenu): before Slot merges anything, it records the onClick the
 *    caller wrote on the element (or `null` for none) under the private prop `callerOnClick`. The button then
 *    calls the merged handler AND runs its command when the caller gave no onClick.
 *  - Tooltip does not Slot onto a command button at all: it hands its content over (`tooltip` prop) and the
 *    button renders its single tooltip with it (the shortcut and the disabled reason stay in the same bubble
 *    instead of two tooltips opening on one button).
 *
 * The button components register themselves here (no import cycle between Tooltip and Button).
 */

import {
  cloneElement,
  isValidElement,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

type ClickHandler = (e: ReactMouseEvent<HTMLButtonElement>) => void;

/** Tooltip settings handed from a wrapping `<Tooltip>` to the button it wraps. */
export interface TooltipHandOff {
  readonly content: ReactNode;
  /** Shortcut source for the bubble (the wrapping Tooltip's `command`); defaults to the button's own command. */
  readonly command?: string | undefined;
  readonly side?: 'top' | 'right' | 'bottom' | 'left' | undefined;
  /** Second line; the button's disabled reason takes its place while the button is disabled. */
  readonly detail?: ReactNode;
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
}

/** Private props of command buttons, set only by the kit's wrappers (never by feature code). */
export interface CommandButtonPrivateProps {
  /**
   * The onClick the caller wrote, recorded before a Radix Slot merged its own handler in: `null` = none,
   * `undefined` = the element was not wrapped (its `onClick` is the caller's).
   */
  readonly callerOnClick?: ClickHandler | null | undefined;
  /** Content and options of a wrapping `<Tooltip>` (see module comment). */
  readonly tooltip?: TooltipHandOff | undefined;
}

interface CommandButtonElementProps extends CommandButtonPrivateProps {
  readonly onClick?: ClickHandler | undefined;
}

const commandButtonTypes = new WeakSet();

/** Called once by Button and IconButton at module load. */
export function registerCommandButton(type: object): void {
  commandButtonTypes.add(type);
}

/** True for a `<Button>` / `<IconButton>` element. */
export function isCommandButtonElement(node: ReactNode): node is ReactElement<CommandButtonElementProps> {
  return isValidElement(node) && typeof node.type !== 'string' && commandButtonTypes.has(node.type);
}

/**
 * Prepares a trigger element for a Radix `asChild` trigger: a command button records its caller's onClick (see
 * module comment); any other node is returned unchanged.
 */
export function asTriggerChild(node: ReactNode): ReactNode {
  if (!isCommandButtonElement(node) || node.props.callerOnClick !== undefined) return node;
  return cloneElement(node, { callerOnClick: node.props.onClick ?? null });
}

/**
 * Whether a click should run the button's command: only when the caller gave no onClick of their own (a handler
 * a Radix trigger merged in does not count).
 */
export function runsCommandOnClick(
  command: string | undefined,
  onClick: ClickHandler | undefined,
  callerOnClick: ClickHandler | null | undefined,
): command is string {
  if (command === undefined) return false;
  return callerOnClick === undefined ? onClick === undefined : callerOnClick === null;
}
